import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/ui/Modal';
import { StateBadge } from '@/components/platform/StateBadge';
import type { ChangeEvent, LifecycleStatus } from '@/types/config-governance';
import {
  allowedLifecycleTransitions,
  lifecycleTransitionRequiresReason,
} from './lifecycle-machine';
import { formatDateTime } from '@/utils/format';
import styles from './platform-pages.module.css';

/**
 * The lifecycle controls academic policies and form schemas share: transition
 * buttons that render LEGAL MOVES ONLY (the state-machine mirror decides), a
 * dialog that demands a reason exactly where the machine does, and the
 * append-only history as a vertical timeline.
 *
 * The buttons are the machine made visible. A screen that offers "Approve" on
 * a DRAFT teaches the operator a lifecycle that does not exist; offering only
 * what POLICY_TRANSITIONS / FORM_TRANSITIONS permit means the 409 path is
 * reserved for genuinely stale pages — and when it fires, the server's own
 * sentence is shown verbatim rather than paraphrased.
 */

interface LifecycleTransitionsProps {
  status: LifecycleStatus;
  /** What the dialog talks about — "PROGRESSION v2", "ADMISSION_STANDARD v3". */
  subject: string;
  /** Named in the activation dialog: what activation supersedes. */
  activationImpact: string;
  /**
   * Performs the move. Resolving closes the dialog; rejecting keeps it open
   * and shows the error's message — the server's own sentence — verbatim.
   */
  perform: (to: LifecycleStatus, reason?: string) => Promise<void>;
}

export function LifecycleTransitions({
  status,
  subject,
  activationImpact,
  perform,
}: LifecycleTransitionsProps) {
  const { t } = useTranslation();
  const [target, setTarget] = useState<LifecycleStatus | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reasonId = useId();

  // A dialog reopened for a different move must not inherit the last reason
  // or the last refusal — that is how the wrong justification lands in an
  // append-only record.
  useEffect(() => {
    setReason('');
    setError(null);
  }, [target]);

  // SUPERSEDED is a legal target on the machine but never a button: it only
  // happens as the side effect of a sibling's activation, and offering it
  // directly would let an operator retire a governing version with nothing to
  // replace it.
  const targets = allowedLifecycleTransitions(status).filter((to) => to !== 'SUPERSEDED');
  if (targets.length === 0) return null;

  const reasonRequired = target ? lifecycleTransitionRequiresReason(target) : false;
  const canConfirm = !busy && (!reasonRequired || reason.trim().length > 0);

  const close = () => {
    if (!busy) setTarget(null);
  };

  const confirm = async () => {
    if (!target) return;
    setBusy(true);
    setError(null);
    try {
      const trimmed = reason.trim();
      await perform(target, trimmed.length > 0 ? trimmed : undefined);
      setTarget(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className={styles.transitionRow}>
        {targets.map((to) => (
          <button
            key={to}
            type="button"
            className={to === 'DRAFT' ? styles.transitionBtnDanger : styles.transitionBtn}
            disabled={busy}
            onClick={() => setTarget(to)}
          >
            {t(`governance.transition.to${to}`)}
          </button>
        ))}
      </div>

      <Modal
        open={target !== null}
        onClose={close}
        title={target ? t(`governance.transition.title.${target}`, { subject }) : ''}
        description={target ? t(`governance.transition.intro.${target}`) : undefined}
        closeOnBackdropClick={false}
      >
        {target === 'ACTIVE' && (
          <ul className={styles.impactList}>
            <li>{activationImpact}</li>
            <li>{t('governance.transition.activeImmutable')}</li>
          </ul>
        )}

        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor={reasonId}>
            {reasonRequired
              ? t('governance.transition.reasonRequired')
              : t('governance.transition.reasonOptional')}
          </label>
          {reasonRequired && (
            <p className={styles.fieldHint}>{t('governance.transition.reasonWhy')}</p>
          )}
          <textarea
            id={reasonId}
            className={styles.textarea}
            rows={3}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            disabled={busy}
            aria-invalid={reasonRequired && reason.length > 0 && reason.trim().length === 0}
          />
        </div>

        {error && (
          <p className={styles.formError} role="alert">
            {error}
          </p>
        )}

        <div className="modal-actions">
          <button type="button" className="modal-button modal-button--secondary" onClick={close} disabled={busy}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className={target === 'DRAFT' ? 'modal-button modal-button--danger' : 'modal-button'}
            disabled={!canConfirm}
            onClick={() => void confirm()}
          >
            {busy
              ? t('governance.transition.working')
              : target
                ? t(`governance.transition.to${target}`)
                : ''}
          </button>
        </div>
      </Modal>
    </>
  );
}

/**
 * GET :id/history rendered as it is stored: append-only events, oldest first,
 * each carrying transition, actor, reason and timestamp. The creation event
 * (fromStatus null) gets its own sentence — a draft comes from nowhere.
 */
export function HistoryTimeline({ events }: { events: ChangeEvent[] }) {
  const { t, i18n } = useTranslation();

  if (events.length === 0) {
    return (
      <p className={styles.sectionEmpty} role="status">
        {t('governance.history.empty')}
      </p>
    );
  }

  return (
    <ol className={styles.timeline}>
      {events.map((event, index) => (
        <li key={event.id || index} className={styles.timelineItem}>
          <span className={styles.timelineDot} aria-hidden="true" />
          <div className={styles.rowMain}>
            <p className={styles.rowTitle}>
              {event.fromStatus === null ? (
                t('governance.history.created')
              ) : (
                <>
                  <StateBadge kind="lifecycle" value={event.fromStatus} size="sm" />
                  {' → '}
                  <StateBadge kind="lifecycle" value={event.toStatus} size="sm" />
                </>
              )}
            </p>
            <p className={styles.rowMetaLine}>
              {t('governance.history.byAt', {
                actor: event.actor || t('governance.history.unknownActor'),
                when: formatDateTime(event.at, i18n.language),
              })}
            </p>
            {event.reason && (
              <p className={styles.rowDetail}>
                {t('governance.history.reason', { reason: event.reason })}
              </p>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}
